<template>
    <span
        class="inline-block"
        :class="{ 'cursor-default': human }"
        :title="human && tooltip ? tooltip : undefined"
    >
        {{ displayText ?? blank }}
    </span>
</template>

<script>
import moment from 'moment-timezone'
import { useSessionStore } from '@/stores/session'

export const DATE_FORMATS = [
    { value: 'ddd MMM D, YYYY', label: 'ddd MMM D, YYYY' },
    { value: 'MMM D, YYYY', label: 'MMM D, YYYY' },
    { value: 'D MMM YYYY', label: 'D MMM YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
]

export const TIME_FORMATS = [
    { value: 'h:mm a', label: 'h:mm a' },
    { value: 'HH:mm:ss', label: 'HH:mm:ss' },
]

const DEFAULT_DATE_FORMAT = 'MMM D, YYYY'
const DEFAULT_TIME_FORMAT = 'h:mm a'
const DEFAULT_TIMEZONE = 'UTC'

export function useDateFormatter() {
    const sessionStore = useSessionStore()

    function getUserData() {
        return sessionStore.data?.user?.data ?? null
    }

    function getAccountData() {
        return sessionStore.data?.account?.data ?? null
    }

    /**
     * Normalizes potentially invalid string values to null
     * Treats null, undefined, empty string, 'null', and 'undefined' as unset
     */
    function normalizeValue(value) {
        if (value === null || value === undefined || value === '') {
            return null
        }
        if (value === 'null' || value === 'undefined') {
            return null
        }
        return value
    }

    /**
     * Validates that a timezone exists in moment-timezone
     */
    function isValidTimezone(tz) {
        return moment.tz.zone(tz) !== null
    }

    function resolveTimezone() {
        const user = getUserData()
        const userTz = normalizeValue(user?.timezone)
        if (userTz && isValidTimezone(userTz)) {
            return userTz
        }
        const account = getAccountData()
        const accountTz = normalizeValue(account?.timezone)
        if (accountTz && isValidTimezone(accountTz)) {
            return accountTz
        }
        return DEFAULT_TIMEZONE
    }

    function resolveDateFormat() {
        const user = getUserData()
        const userFormat = normalizeValue(user?.date_format)
        if (userFormat) return userFormat
        const account = getAccountData()
        const accountFormat = normalizeValue(account?.date_format)
        if (accountFormat) return accountFormat
        return DEFAULT_DATE_FORMAT
    }

    function resolveTimeFormat() {
        const user = getUserData()
        const userFormat = normalizeValue(user?.time_format)
        if (userFormat) return userFormat
        const account = getAccountData()
        const accountFormat = normalizeValue(account?.time_format)
        if (accountFormat) return accountFormat
        return DEFAULT_TIME_FORMAT
    }

    function formatDate(value) {
        if (!value) return null
        return moment
            .utc(value)
            .tz(resolveTimezone())
            .format(resolveDateFormat())
    }

    function formatDateTime(value) {
        if (!value) return null
        const tz = resolveTimezone()
        const df = resolveDateFormat()
        const tf = resolveTimeFormat()
        return moment.utc(value).tz(tz).format(`${df} ${tf}`)
    }

    function formatDateTimeTz(value) {
        if (!value) return null
        const tz = resolveTimezone()
        const df = resolveDateFormat()
        const tf = resolveTimeFormat()
        return moment.utc(value).tz(tz).format(`${df} ${tf} z`)
    }

    function formatHuman(value) {
        if (!value) return null
        const text = moment.utc(value).fromNow()
        return text.charAt(0).toUpperCase() + text.slice(1)
    }

    function getTimezoneAbbr(value) {
        if (!value) return ''
        return moment.utc(value).tz(resolveTimezone()).format('z')
    }

    return {
        resolveTimezone,
        resolveDateFormat,
        resolveTimeFormat,
        formatDate,
        formatDateTime,
        formatDateTimeTz,
        formatHuman,
        getTimezoneAbbr,
        DATE_FORMATS,
        TIME_FORMATS,
    }
}
</script>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    blank: {
        type: String,
        default: '--',
    },
    format: {
        type: String,
        default: 'short',
    },
    human: {
        type: Boolean,
        default: false,
    },
    humanThresholdDays: {
        type: Number,
        default: 30,
    },
    tz: {
        type: Boolean,
        default: false,
    },
    utc: {
        type: Boolean,
        default: false,
    },
    value: {
        type: String,
        default: null,
    },
})

const {
    formatDate,
    formatDateTime,
    formatHuman,
    getTimezoneAbbr,
    resolveDateFormat,
    resolveTimeFormat,
    resolveTimezone,
} = useDateFormatter()

const formatted = computed(() => {
    if (!props.value) return null

    let result
    if (props.utc) {
        const m = moment.utc(props.value)
        switch (props.format) {
            case 'date':
                result = m.format(resolveDateFormat())
                break
            case 'time':
                result = m.format(resolveTimeFormat())
                break
            default:
                result = m.format(
                    `${resolveDateFormat()} ${resolveTimeFormat()}`
                )
                break
        }
    } else {
        switch (props.format) {
            case 'date':
                result = formatDate(props.value)
                break
            case 'time':
                result = moment
                    .utc(props.value)
                    .tz(resolveTimezone())
                    .format(resolveTimeFormat())
                break
            default:
                result = formatDateTime(props.value)
                break
        }
    }

    if (props.tz && result) {
        result += ` ${getTimezoneAbbr(props.value)}`
    }

    return result
})

const isBeyondHumanThreshold = computed(() => {
    if (!props.value) return false
    return (
        moment.utc().diff(moment.utc(props.value), 'days') >=
        props.humanThresholdDays
    )
})

const displayText = computed(() => {
    if (props.value && props.human && !isBeyondHumanThreshold.value) {
        return formatHuman(props.value)
    }
    return formatted.value
})

const tooltip = computed(() => formatted.value ?? '')
</script>
